<template lang="pug">
div
  v-form(ref="form")
    slot(name="title")
    template(v-if="!appointment")
      div.text-sm.font-semibold.neutral500--text.mt-4 Search patient
      patient-nric-dropdown.mt-2(v-model="patientObj" @change="fetchPatientInfo(patientObj)")
    template(v-if="showForm")
      div.d-flex.justify-center.mt-16(v-if="isFetchingInfo || isFetchingPatient")
        v-progress-circular(:width="2" :size="50" color="primary" indeterminate)
      template(v-else)
        template(v-if="patient.id")
          v-card.mt-4.pa-4.round-lg(outlined)
            v-row(dense)
              v-col(cols="6")
                div.text-xs.neutral500--text {{ $t('name') }}
                div.text-sm.neutral700--text.mt-1 {{ patient.name || '-' }}
              v-col(cols="6")
                div.text-xs.neutral500--text {{ $t('nric_passport') }}
                div.text-sm.neutral700--text.mt-1 {{ patient.ic || '-' }}
              v-col.pt-3(cols="6")
                div.text-xs.neutral500--text {{ $t('email') }}
                div.text-sm.neutral700--text.mt-1 {{ patient.email || '-' }}
              v-col.pt-3(cols="6")
                div.text-xs.neutral500--text {{ $t('phone_number') }}
                div.text-sm.neutral700--text.mt-1 {{ patient.phone || '-' }}
              v-col.pt-3(cols="12")
                div.text-xs.neutral500--text {{ $t('address') }}
                div.text-sm.neutral700--text.mt-1 {{ checkEmptyAddress(patient.address) ? '-' : getAddressInString(patient.address) }}

        template(v-else)
          div.text-sm.font-semibold.neutral500--text.mt-4 {{ $t('name') }}
          v-text-field.text-sm.mt-2.round-xl(v-model="patient.name" :placeholder="$t('name')" outlined dense height="48" :rules="rules.not_empty" hide-details="auto")
          div.text-sm.font-semibold.neutral500--text.mt-4 {{ $t('phone') }}
          phone-input.mt-2(ref="phoneInput" v-model="patient.phone" :required="!defaultIC" :loading="isFetchingField" @change="checkIcPhone(patient.phone, 'phone')")
          div.text-sm.font-semibold.neutral500--text.mt-4 {{ $t('nric_passport') }}
          v-text-field.text-sm.mt-2.round-xl(v-model="patient.ic" :placeholder="$t('enter_nric_passport')" outlined dense height="48" hide-details="auto" :loading="isFetchingField" :rules="defaultIC ? rules.not_empty : []" @input="checkIcPhone(patient.ic, 'ic')")
          div.text-sm.font-semibold.neutral500--text.mt-4 {{ $t('email') }}
          v-text-field.text-sm.mt-2.round-xl(v-model="patient.email" :placeholder="$t('email_optional')" outlined dense height="48" hide-details="auto" :rules="Boolean(patient.email) ? rules.valid_email : []")
          .address
            div.text-sm.font-semibold.neutral500--text.mt-4 {{$t('address_1.label')}}
            v-text-field.text-sm.mt-2.round-xl(v-model="address.address_1" :placeholder="$t('address_1.placeholder')" outlined dense height="48" :rules="emptyAddress ? [] : rules.not_empty" hide-details="auto")
            div.text-sm.font-semibold.neutral500--text.mt-4 {{$t('address_2.label')}}
            v-text-field.text-sm.mt-2.round-xl(v-model="address.address_2" :placeholder="$t('address_2.placeholder')" outlined dense height="48" hide-details="auto")
            div.text-sm.font-semibold.neutral500--text.mt-4 {{$t('postcode.label')}}
            v-text-field.text-sm.mt-2.round-xl(v-model="address.postcode" :placeholder="$t('postcode.placeholder')" outlined dense height="48" :rules="emptyAddress ? [] : rules.not_empty" hide-details="auto" @keyup="fetchPostcodes(address.postcode)")
            div.text-sm.font-semibold.neutral500--text.mt-4 {{$t('city.label')}}
            v-text-field.text-sm.mt-2.round-xl(v-model="address.city" :placeholder="$t('city.placeholder')" outlined dense height="48" :rules="emptyAddress ? [] : rules.not_empty" hide-details="auto" :loading="isFetchingAddress")
            div.text-sm.font-semibold.neutral500--text.mt-4 {{$t('state.label')}}
            v-text-field.text-sm.mt-2.round-xl(v-model="address.state" :placeholder="$t('state.placeholder')" outlined dense height="48" :rules="emptyAddress ? [] : rules.not_empty" hide-details="auto" :loading="isFetchingAddress")
            div.text-sm.font-semibold.neutral500--text.mt-4 {{$t('country.label')}}
            v-text-field.text-sm.mt-2.round-xl(v-model="address.country" :placeholder="$t('country.placeholder')" outlined dense height="48" :rules="emptyAddress ? [] : rules.not_empty" hide-details="auto" :loading="isFetchingAddress")

</template>
<script>
import { mapGetters, mapActions } from 'vuex'
import { debounce } from '@/utils'
import PatientNricDropdown from '@/components/clinic/appointment/custom/PatientNricDropdown.vue'
import PhoneInput from '@/components/clinic/custom/PhoneInput.vue'
import CompanyDropdownSingleSelect from '@/components/hq/custom/CompanyDropdownSingleSelect.vue'

export default {
  name: 'AppointmentPatient',
  props: ['appointment', 'prefill'],
  components: { PhoneInput, PatientNricDropdown, CompanyDropdownSingleSelect },
  data () {
    return {
      isFetchingPatient: false,
      isFetchingAddress: false,
      isFetchingInfo: false,
      isFetchingField: false,
      showForm: false,
      patient: {},
      address: {},
      patients: [],
      patientObj: null,
      rules: {
        not_empty: [v => (v != null && v.toString().trim().length) || 'This field is required'],
        has_quantity: [v => !!v || 'This field is required', v => v != 0 || 'This field is required'],
        has_price: [(v) => !!v || 'This field is required', (v) => v >= 1|| `Amount should be more than ${this.$currency.symbol} 1`],
        not_empty_select: [v => !!v || 'This field is required'],
        valid_email: [ v => /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(v) || 'E-mail must be valid', ]
      },
    }
  },
  computed: {
    ...mapGetters({
      company: 'getCompany',
      clinic: 'clinic/getClinic',
      isHq: 'hq/getIsHq',
      hqSelectedCompany: 'hq/getSelectedCompany'
    }),
    patientData () {
      return {
        patient: this.patient,
        address: this.address
      }
    },
    emptyAddress () {
      if (Object.keys(this.address).length === 0) {
        return true
      } else if (Object.values(this.address).every(val => !val)) {
        return true
      }
      return false
    },
    defaultIC () {
      if (this.clinic && this.clinic.customer_uid_type === 'ic') {
        return true
      }
      return false
    },
    isHqAndNoCompanySelected () {
      return this.isHq && !this.hqSelectedCompany
    }
  },
  watch: {
    patientData: {
      deep: true,
      handler (val) {
        this.$store.commit('clinic/updateTempAppointment', val)
      } 
    },
    hqSelectedCompany(newVal, oldVal) {
      if (newVal !== oldVal) {
        // Reset all data
        this.patient = {}
        this.address = {}
        this.patientObj = null
        this.showForm = false
        this.isFetchingInfo = false
        this.isFetchingPatient = false
        this.isFetchingField = false
      }
    }
  },
  mounted () {
    if (this.appointment) {
      this.setData()
    } if (this.prefill) {
      this.setPrefillData()
    }
  },
  methods: {
    ...mapActions(['showMessage']),
    setData () {
      this.patientObj = {
        id: this.appointment.patient.id,
        name: this.appointment.patient.name,
      }
      this.patient = {
        name: this.appointment.patient.name,
        phone: this.appointment.patient.phone,
        email: this.appointment.patient.email,
        ic: this.appointment.patient.ic
      }
      if (!this.checkEmptyAddress(this.appointment.patient.address)) {
        this.address = this.appointment.patient.address
      }
      this.showForm = true
    },
    setPrefillData () {
      this.patientObj = {
        id: this.prefill?.id || null,
        name: this.prefill?.name || null
      }
      this.fetchPatientInfo(this.patientObj)
    },
    fetchPatientInfo (patientObj) {
      this.patient = {}
      this.address = {}
      this.showForm = true
      if (patientObj && patientObj.id) {
        this.isFetchingInfo = true
        const companySlug = this.isHq ? this.hqSelectedCompany.slug : this.company.slug
        this.$axios.get(`/clinics/${companySlug}/patients/${patientObj.id}`)
          .then(resp => {
            this.patient = resp.data
            if (resp.data.address) {
              this.address = resp.data.address
            }
          })
          .catch(() => {})
          .finally(() => {
            this.isFetchingInfo = false
          })
      }
    },
    fetchPostcodes (val) {
      if (!val) {
        return
      }
      if (val.length !== 5) {
        return
      }
      this.isFetchingAddress = true
      this.$axios.get(`/shipping/postcodes?postcode=${this.address.postcode}`).then((response) => {
        const addresses = response.data
        if (addresses.length > 0 && addresses[0].postcode) {
          this.$set(this.address, 'city', addresses[0].city)
          this.$set(this.address, 'state', addresses[0].state)
          this.$set(this.address, 'country', addresses[0].country)
        }
      })
      .catch(() => {
      })
      .finally(() => {
        this.isFetchingAddress = false
      })
    },
    fetchCheckPatient: debounce(function(params) {
      this.isFetchingField = true
      const companySlug = this.isHq ? this.hqSelectedCompany.slug : this.company.slug
      this.$axios.get(`/clinics/${companySlug}/patients/`, { params })
        .then(resp => {
          if (resp.data.results.length === 1) {
            this.showMessage(['info', 'Existing patient record detected'])
            this.patientObj = {
              id: resp.data.results[0].id,
              name: resp.data.results[0].name,
            }
            this.patient = resp.data.results[0]
            if (resp.data.results[0].address) {
              this.address = resp.data.results[0].address
            }
          }
        })
        .catch(() => {})
        .finally(() => {
          this.isFetchingField = false
      })
    }, 750),
    checkIcPhone (data, source) {
      if (!data) { return }
      this.sanitizePhone(data, source)
      if (this.defaultIC && source === 'ic') {
        this.fetchCheckPatient({ ic: data })
      } 
      if (!this.defaultIC && source === 'phone') {
        this.fetchCheckPatient({ phone: data })
      }
    },
    checkEmptyAddress (address) {
      if (!address) {
        return true
      }
      if (Object.keys(address).length === 0) {
        return true
      } else if (Object.values(address).every(val => !val)) {
        return true
      }
      return false
    },
    getAddressInString (addr) {
      let address = ''
      if (addr.address_1) {
        address += addr.address_1
      } if (addr.address_2) {
        address += `, ${addr.address_2}`
      } if (addr.postcode) {
        address += `, ${addr.postcode}`
      } if (addr.city) {
        address += `, ${addr.city}`
      } if (addr.state) {
        address += `, ${addr.state}`
      } if (addr.country) {
        address += `, ${addr.country}`
      }
      return address
    },
    sanitizePhone (phone, source) {
      if (phone && source === 'phone') {
        let sphone = phone.replace(/\D/g, '')
        this.$set(this.patient, 'phone', sphone) 
      }
    },
  }
}
</script>

<style lang="scss" scoped>
::v-deep {
  .v-text-field--outlined {
    fieldset {
      border-color: var(--v-neutral400);
    }
  } 
  .error--text {
    color: var(--v-danger500) !important;
    fieldset {
      border-color: var(--v-danger500);
    }
  }
  .combostyle .v-input__icon {
    min-width: 22px;
    width: 22px;
    margin-top: 4px;
  }
}
</style>

<i18n>
{
  "en": {
    "address_1": {
      "label": "Address Line 1",
      "placeholder": "e.g. K-3-01, Conezion, Persiaran IRC3"
    },
    "address_2": {
      "label": "Address Line 2",
      "placeholder": "e.g. IOI Resort City"
    },
    "postcode": {
      "label": "Postcode",
      "placeholder": "e.g. 62502"
    },
    "city": {
      "label": "City",
      "placeholder": "e.g. Putrajaya"
    },
    "state": {
      "label": "State",
      "placeholder": "e.g. Wilayah Persekutuan Putrajaya"
    },
    "country": {
      "label": "Country",
      "placeholder": "e.g. Malaysia"
    }
  },
  "id": {
   "address_1": {
      "label": "Nama Jalan, Gedung, No Rumah, RT/RW, Kelurahan",
      "placeholder": "e.g. Jalan Prof. Joko Sutono Sh No.999, RT.14/RW.6, Petogogan, "
    },
    "address_2": {
      "label": "Kecamatan",
      "placeholder": "Kec. Kby. Baru"
    },
    "postcode": {
      "label": "Kode Pos",
      "placeholder": "12160"
    },
    "city": {
      "label": "Kota",
      "placeholder": "Kota Jakarta Selatan"
    },
    "state": {
      "label": "Provinsi",
      "placeholder": "DKI Jakarta"
    },
    "country": {
      "label": "Country",
      "placeholder": "e.g. Indonesia"
    }
  }
}
</i18n>